import {
  AcademicCapIcon,
  StarIcon,
  ChatBubbleLeftRightIcon,
  TrophyIcon,
  FireIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  SparklesIcon,
  HeartIcon,
  BoltIcon,
  ShieldCheckIcon,
  BookOpenIcon,
  DocumentCheckIcon,
  HandThumbUpIcon,
  ClockIcon,
  CalendarDaysIcon,
  UsersIcon,
  FlagIcon,
  PuzzlePieceIcon,
  GiftIcon,
} from '@heroicons/react/24/outline'

// Mapping from icon name to HeroIcon component
const BadgeIconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  'academic-cap': AcademicCapIcon,
  'star': StarIcon,
  'chat': ChatBubbleLeftRightIcon,
  'trophy': TrophyIcon,
  'fire': FireIcon,
  'lightbulb': LightBulbIcon,
  'light-bulb': LightBulbIcon,
  'rocket': RocketLaunchIcon,
  'sparkles': SparklesIcon,
  'heart': HeartIcon,
  'bolt': BoltIcon,
  'shield': ShieldCheckIcon,
  'book': BookOpenIcon,
  'document': DocumentCheckIcon,
  'thumbup': HandThumbUpIcon,
  'clock': ClockIcon,
  'calendar': CalendarDaysIcon,
  'users': UsersIcon,
  'flag': FlagIcon,
  'puzzle': PuzzlePieceIcon,
  'gift': GiftIcon,
}

// Badge icon colors based on icon type
const iconColors: Record<string, string> = {
  'academic-cap': 'text-blue-600',
  'star': 'text-yellow-500',
  'chat': 'text-green-600',
  'trophy': 'text-yellow-600',
  'fire': 'text-orange-500',
  'lightbulb': 'text-amber-500',
  'light-bulb': 'text-amber-500',
  'rocket': 'text-purple-600',
  'sparkles': 'text-pink-500',
  'heart': 'text-red-500',
  'bolt': 'text-yellow-400',
  'shield': 'text-emerald-600',
  'book': 'text-indigo-600',
  'document': 'text-slate-600',
  'thumbup': 'text-blue-500',
  'clock': 'text-gray-600',
  'calendar': 'text-teal-600',
  'users': 'text-violet-600',
  'flag': 'text-red-600',
  'puzzle': 'text-cyan-600',
  'gift': 'text-pink-600',
}

// Check if string is an emoji
const isEmoji = (str: string): boolean => {
  const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u
  return emojiRegex.test(str)
}

// Extract icon name from URL or use directly
const getIconName = (icon?: string): string => {
  if (!icon) return ''
  // If it's a URL, extract the last segment (icon name)
  if (icon.startsWith('http') || icon.startsWith('/')) {
    const parts = icon.split('/')
    return parts[parts.length - 1] || ''
  }
  return icon
}

interface BadgeIconProps {
  icon?: string
  className?: string
  fallbackClassName?: string
}

export function BadgeIcon({ icon, className = "w-8 h-8", fallbackClassName }: BadgeIconProps) {
  const iconName = getIconName(icon)

  // If no icon, show default trophy
  if (!iconName) {
    return <TrophyIcon className={`${className} text-yellow-500`} />
  }

  // If it's an emoji, display it as text
  if (isEmoji(iconName)) {
    return <span className={className}>{iconName}</span>
  }

  // Get the HeroIcon component
  const iconKey = iconName.toLowerCase()
  const IconComponent = BadgeIconMap[iconKey]
  const colorClass = iconColors[iconKey] || 'text-yellow-500'

  if (IconComponent) {
    return <IconComponent className={`${className} ${fallbackClassName || colorClass}`} />
  }

  // Fallback: if not found in map, show as text (might be an emoji or unknown icon)
  return <span className={className}>{iconName}</span>
}

export default BadgeIcon
